/**
 * 判断某节点的所有后代中是否有人被选中
 * @param node 当前节点
 * @param checkList 已选中的人员列表
 * @returns 是否至少有一个子节点被选中
 */
const hasAnyCheckedDescendant = (node, checkList) => {
  if (!node.children || node.children.length === 0) return false
  return node.children.some(child => {
    // 如果当前子节点在已选列表中，返回 true
    if (checkList.some(i => i.key === child.key)) return true
    // 递归检查后代
    return hasAnyCheckedDescendant(child, checkList)
  })
}

/**
 * 获取某个节点下的所有后代用户（包含多层嵌套）
 * @param item 当前部门节点
 * @returns 所有叶子用户和子部门的集合（不含空部门）
 */
const getAllDescendantUsers = item => {
  let result = []
  if (!item.children || item.children.length === 0) {
    result.push(item)
  } else {
    for (const child of item.children) {
      // 忽略空部门（children 是空数组）
      if (child.children && child.children.length === 0) continue
      result = result.concat(getAllDescendantUsers(child))
    }
  }
  return result
}

/**
 * 获取某节点下的所有“叶子用户”节点（即无 children 的成员节点）
 * @param node 当前节点
 * @returns 所有最终人员节点（不含子部门）
 */
const getAllLeafUsers = node => {
  const all = getAllDescendantUsers(node)
  // 只保留无 children 的叶子节点（人员）
  return all.filter(n => !n.children || n.children.length === 0)
}

/**
 * 判断某节点下所有的“叶子用户”是否都已被勾选
 * @param node 当前节点
 * @param checkList 已勾选成员列表
 * @returns 是否全部勾选
 */
const areAllLeafUsersChecked = (node, checkList) => {
  const leafUsers = getAllLeafUsers(node)
  // 筛选出没有 children 的节点
  return leafUsers.length > 0 && leafUsers.every(user => checkList.some(i => i.key === user.key))
}

/**
 * 递归生成树结构
 * @param data 原始部门和成员数据
 * @returns { treeData, totalUsers }：树结构数据和总成员数
 */
const generateTree = data => {
  const map = {} // 临时映射表
  let totalUsers = 0 // 👈 新增总人数计数器

  // 处理扁平化的用户列表数据 - 搜索人员
  if (Array.isArray(data) && data.length > 0 && 'lenovo_id' in data[0]) {
    totalUsers = data.length
    return {
      treeData: data.map(user => ({
        title: user.user_name,
        key: user.lenovo_id,
        isLeaf: true,
        avatar: user?.avatar || '',
        disabled: user.disabled
      })),
      totalUsers
    }
  }

  const traverse = items => {
    return items.map(item => {
      const node = {
        title: item.depart_name || '暂无数据', // 默认标题为 '暂无数据'
        key: item.depart_id,
        children: [],
        disabled: false // 默认不禁用
      }
      map[item.depart_id] = item.depart_name || '暂无数据' // 添加到映射表

      // 先处理子部门
      const hasSubDepartments = Array.isArray(item.nodes_depart) && item.nodes_depart.length > 0
      if (hasSubDepartments) {
        node.children = node.children.concat(traverse(item.nodes_depart))
      }

      // 再处理部门下的成员列表
      const hasMembers = Array.isArray(item.user_list) && item.user_list.length > 0
      if (hasMembers) {
        totalUsers += item.user_list.length // 👈 累加成员数量
        node.children = node.children.concat(
          item.user_list.map(user => {
            map[user.lenovo_id] = user.user_name // 添加到映射表
            return {
              title: user.user_name,
              key: user.lenovo_id,
              isLeaf: true,
              avatar: user?.avatar || '', // 用户头像
              disabled: user.disabled // 成员是否禁用
            }
          })
        )
      }

      // 如果该部门没有成员和子部门，标记为禁用
      if (!hasMembers && !hasSubDepartments) {
        node.disabled = true
        node.title = `${node.title}（暂无数据）`
      }

      return node
    })
  }

  // 处理 roleOrganization 接口返回的数据结构
  if (data && Array.isArray(data) && data.length > 0 && 'depart_id' in data[0]) {
    const firstLevel = data[0]
    const result = []

    // 先处理第一层的部门（跳过公司层）
    if (Array.isArray(firstLevel.nodes_depart) && firstLevel.nodes_depart.length > 0) {
      // 直接处理子部门，不创建公司节点
      result.push(
        ...firstLevel.nodes_depart.map(item => {
          const node = {
            title: item.depart_name || '暂无数据',
            key: item.depart_id,
            children: [],
            disabled: false
          }

          // 先处理子部门
          if (Array.isArray(item.nodes_depart) && item.nodes_depart.length > 0) {
            node.children = node.children.concat(traverse(item.nodes_depart))
          }

          // 再处理部门下的成员列表
          if (Array.isArray(item.user_list) && item.user_list.length > 0) {
            totalUsers += item.user_list.length
            node.children = node.children.concat(
              item.user_list.map(user => ({
                title: user.user_name,
                key: user.lenovo_id,
                isLeaf: true,
                avatar: user?.avatar || '',
                disabled: user.disabled
              }))
            )
          }

          // 如果该部门没有成员和子部门，标记为禁用
          if (!item.user_list?.length && !item.nodes_depart?.length) {
            node.disabled = true
            node.title = `${node.title}（暂无数据）`
          }

          return node
        })
      )
    }

    // 再处理第一层的用户（跳过公司层）
    if (Array.isArray(firstLevel.user_list) && firstLevel.user_list.length > 0) {
      totalUsers += firstLevel.user_list.length
      result.push(
        ...firstLevel.user_list.map(user => ({
          title: user.user_name,
          key: user.lenovo_id,
          isLeaf: true,
          avatar: user?.avatar || '',
          disabled: user.disabled
        }))
      )
    }

    return {
      treeData: result,
      totalUsers
    }
  }
  // 处理其他数据结构
  const treeData = traverse(Array.isArray(data) ? data : [])
  return {
    treeData, // 生成的树形结构数据
    totalUsers // 总成员数
  }
}

export { areAllLeafUsersChecked, generateTree, getAllDescendantUsers, getAllLeafUsers, hasAnyCheckedDescendant }
